/**
 * 앱이 공유하는 계측 로그 (SPEC S-10, TASK-2026-004 R2-REV-001).
 *
 * 화면 여러 곳에서 같은 로그에 기록해야 하므로 기록 출처마다 단일 인스턴스로 둔다.
 * 출처는 연습(로컬)이거나 서버에서 확인한 참가 세션이며, 세션 로그는 세션 키 공간에만
 * 쓴다(`recordingScope.ts`). 저장 확인 기록 버퍼도 로그마다 따로라서, 연습·앞선 세션의
 * 저장 판정과 손실 수가 새 세션의 사실로 섞이지 않는다.
 *
 * 브라우저 저장소가 막혀 있으면 `resolveBrowserStorage`가 메모리 저장소로 물러나므로,
 * 호출자는 저장 가능 여부를 신경 쓰지 않아도 된다.
 */

import { signalRecorded } from '../online/flushSignal';
import { PAGE_CLOCK } from '../online/clock';
import { createConfirmationBuffer } from '../telemetry/confirmation';
import {
  createConfirmationSpool,
  type ConfirmationSpool,
  type LedgerStorage,
  type SpoolBackend,
} from '../telemetry/confirmationSpool';
import { createIndexedDbSpoolBackend } from '../telemetry/indexedDbSpool';
import { createTelemetryLog, resolveBrowserStorage, type TelemetryLog } from '../telemetry/store';
import { recordingPaused, recordingSessionRef, scopedStorage, setRecordingSession } from './recordingScope';

export { recordingSessionRef, setRecordingSession };

interface Recording {
  readonly log: TelemetryLog;
  /**
   * 참가 세션 기록의 저장 확인 기록 spool (CR-01 A+, ADR-002).
   *
   * 연습 기록은 전송하지 않으므로 spool이 없다(`undefined`). 연습 로그의 확인 기록은
   * 메모리 버퍼에만 남는다.
   */
  readonly spool: ConfirmationSpool | undefined;
}

/**
 * spool 의존성. 기본은 브라우저 IndexedDB와 localStorage다.
 *
 * 검사가 메모리 backend·ledger·시계를 주입한다. 상한도 여기서만 바꾼다 —— production URL
 * 인자나 전역 flag로 바꾸는 길은 두지 않는다.
 */
export interface ConfirmationSpoolConfig {
  readonly backend: SpoolBackend | undefined;
  readonly ledger: LedgerStorage;
  readonly now: () => number;
  readonly capacity?: number;
}

let config: ConfirmationSpoolConfig | undefined;

export function configureConfirmationSpool(next: ConfirmationSpoolConfig): void {
  config = next;
}

/** localStorage를 ledger로 쓴다. 접근 자체가 던지면 모든 호출이 던져 `ledger_unavailable`이 된다. */
function browserLedger(): LedgerStorage {
  const storage = (): Storage => window.localStorage;
  return {
    getItem: (key) => storage().getItem(key),
    setItem: (key, value) => storage().setItem(key, value),
    removeItem: (key) => storage().removeItem(key),
    keys: () => {
      const out: string[] = [];
      const store = storage();
      for (let index = 0; index < store.length; index += 1) {
        const key = store.key(index);
        if (key !== null) {
          out.push(key);
        }
      }
      return out;
    },
  };
}

function defaultConfig(): ConfirmationSpoolConfig {
  config ??= { backend: createIndexedDbSpoolBackend(), ledger: browserLedger(), now: () => Date.now() };
  return config;
}

/** 이 페이지 실행의 식별자. runtime ledger 키가 쓴다. */
const RUNTIME_ID = `rt${Date.now().toString(36)}${Math.floor(Math.random() * 1e9).toString(36)}`;

const LOCAL = '';
const recordings = new Map<string, Recording>();

function recording(sessionRef: string | undefined): Recording {
  const id = sessionRef ?? LOCAL;
  const existing = recordings.get(id);
  if (existing !== undefined) {
    return existing;
  }
  const settings = defaultConfig();
  const spool =
    sessionRef === undefined
      ? undefined
      : createConfirmationSpool({
          sessionRef,
          backend: settings.backend,
          ledger: settings.ledger,
          runtimeId: RUNTIME_ID,
          now: settings.now,
          ...(settings.capacity === undefined ? {} : { capacity: settings.capacity }),
        });
  const localConfirmations = spool === undefined ? createConfirmationBuffer() : undefined;
  /*
   * 물러났다는 사실을 함께 넘긴다. 이것이 없으면 저장소가 막힌 환경에서도
   * 로그가 스스로를 완전하다고 여긴다 —— 메모리 저장소는 쓰기가 실패하지
   * 않으므로 로그 입장에서는 아무 문제가 없어 보인다(REV-014).
   */
  const resolved = resolveBrowserStorage();
  const log = createTelemetryLog({
    storage: sessionRef === undefined ? resolved.storage : scopedStorage(resolved.storage, sessionRef),
    persistent: resolved.persistent,
    // epoch 시각이 바뀌어도 같은 page 안의 시간 차를 판정할 수 있게 함께 고정한다.
    clock: () => PAGE_CLOCK.event(),
    /*
     * 확인 기록은 spool의 sink로만 간다. spool 저장은 이 로그의 저장 hook을 거치지 않으므로
     * 확인 기록이 확인 기록을 다시 만들지 않는다(CR-01 A+ §2.1.6).
     */
    onConfirmation: spool !== undefined ? (record) => spool.sink(record) : localConfirmations?.sink,
  });
  /*
   * 목표 오픈·시도 종료는 기록되자마자 보낸다 (REV-003).
   *
   * 기록하는 자리마다 전송 호출을 흩으면 하나를 빠뜨렸을 때 그 구간만 조용히
   * 늦게 간다. 기록의 유일한 입구를 한 겹 감싸 신호를 보낸다. 듣는 전송기가
   * 없으면(연습 모드) 아무 일도 일어나지 않는다.
   */
  const created: Recording = {
    spool,
    log: {
      ...log,
      record(name, payload) {
        // 다시 확인하는 동안에는 남기지 않는다 (CR-03, `recordingScope.pauseRecording`).
        if (recordingPaused()) {
          return undefined;
        }
        const event = log.record(name, payload);
        if (event !== undefined) {
          signalRecorded(name);
        }
        return event;
      },
    },
  };
  recordings.set(id, created);
  return created;
}

/** 지금 기록하는 출처의 로그. 화면은 이것만 쓴다. */
export function telemetry(): TelemetryLog {
  return recording(recordingSessionRef()).log;
}

/** 특정 출처의 로그. 전송기는 자기 세션을 이름으로 집어 쓴다. `undefined`는 연습이다. */
export function telemetryFor(sessionRef: string | undefined): TelemetryLog {
  return recording(sessionRef).log;
}

/** 참가 세션의 저장 확인 기록 spool. 전송기·저장 상태 표시가 쓴다. */
export function confirmationSpoolFor(sessionRef: string): ConfirmationSpool {
  const spool = recording(sessionRef).spool;
  if (spool === undefined) {
    throw new Error('세션 기록에는 spool이 있어야 합니다.');
  }
  return spool;
}
